import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { subscribeToEmailMarketingSchema as subscribeToEmailMarketing } from "@redotech/redo-model/marketing/schemas/subscribe-to-email-marketing-schema";
import {
  DEFAULT_NUM_VIEWED_PRODUCTS_UNTIL_PANEL_OPEN,
  DEFAULT_PANEL_MESSAGE,
  DEFAULT_PANEL_TITLE,
} from "@redotech/redo-model/slide-in-panel/save-for-later-util";
import {
  DEFAULT_SAVE_FOR_LATER_PANEL_ANCHOR_LOCATION,
  SaveForLaterSlideInPanelAnchorLocation,
} from "@redotech/redo-model/slide-in-panel/slide-in-panel-anchor-location";
import { ButtonSize } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { ExternalLink } from "@redotech/redo-web/link";
import { ExternalLogin } from "@redotech/redo-web/login/external-login";
import { RedoLogin } from "@redotech/redo-web/login/redo-login";
import { Text } from "@redotech/redo-web/text";
import { setLocalStorageJson } from "@redotech/redo-web/utils/local-storage-wrapper";
import { CUSTOMER_WIDGET_TOKEN_KEY } from "@redotech/redo-web/utils/shared-conf";
import { parseAuth } from "@redotech/redo-web/utils/storefront-is-logged-in";
import { ShopifyClientType } from "@redotech/shopify-client/ajax";
import { addProductsToCustomerProductList } from "@redotech/shopify-sdk/rpc/schema/add-products-to-customer-product-list";
import { createOneTimePasscode } from "@redotech/shopify-sdk/rpc/schema/create-one-time-passcode";
import { getCustomerAuthFromCustomerWidgetAuth } from "@redotech/shopify-sdk/rpc/schema/get-customer-auth-from-customer-widget-auth";
import { getShopifyExtensionTeamInfo } from "@redotech/shopify-sdk/rpc/schema/get-shopify-extension-team-info";
import { verifyOneTimePasscode } from "@redotech/shopify-sdk/rpc/schema/verify-one-time-passcode";
import { isDef } from "@redotech/util/checks";
import { useContext, useEffect, useMemo, useState } from "react";
import { NotLoggedInFavoriteProductManager } from "../../app-blocks/favorite-product-button/favorite-product-manager/not-logged-in-favorite-product-manager";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import {
  ExtensionShopifyClientContext,
  isOnProductPage,
  shopifyProductHandle,
} from "../../shopify";
import { getVariantIdFromUrl } from "../../utils";
import { SlideInPanel } from "../slide-in-panel/slide-in-panel";
import {
  SaveForLaterCache,
  SaveForLaterProductData,
} from "./save-for-later-cache";
import * as saveForLaterCss from "./save-for-later.module.css";

const getViewSavedItemsUrl = (
  customerAuthToken: string,
  customer: {
    email: string;
    name?: string | undefined;
    customerId?: string | undefined;
  },
  storeUrl: string,
): string => {
  const url = new URL(`https:/${storeUrl}/apps/redo/returns-portal`);
  url.searchParams.set("customer", JSON.stringify(customer));
  url.searchParams.set("token", customerAuthToken);
  url.searchParams.set("next", "/returns-portal/account/lists");

  return url.toString();
};

export interface SaveForLaterPanelSettings {
  enabled: boolean;
  numViewedProductsUntilPanelOpen?: number | undefined;
  title?: string | undefined;
  message?: string | undefined;
  anchorLocation?: SaveForLaterSlideInPanelAnchorLocation | undefined;
}

enum SaveForLaterSlideInPanelState {
  LOGIN_TO_SAVE_FOR_LATER = "LOGIN_TO_SAVE_FOR_LATER",
  SUCCESS_CONFIRMATION = "SUCCESS_CONFIRMATION",
}

export const SaveForLaterSlideInPanel = ({
  saveForLaterPanelSettings,
  saveForLaterMarketingEnabled,
}: {
  saveForLaterPanelSettings: SaveForLaterPanelSettings;
  saveForLaterMarketingEnabled: boolean;
}) => {
  const extensionShopifyClient = useRequiredContext(
    ExtensionShopifyClientContext,
  );
  const redoShopifyRpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const storefrontClient = useContext(ExtensionShopifyClientContext);

  const onTapcart = extensionShopifyClient.type === ShopifyClientType.TAPCART;

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelState, setPanelState] = useState<SaveForLaterSlideInPanelState>(
    SaveForLaterSlideInPanelState.LOGIN_TO_SAVE_FOR_LATER,
  );
  const [viewSavedItemsUrl, setViewSavedItemsUrl] = useState<
    string | undefined
  >(undefined);
  const [productsToSaveForLater, setProductsToSaveForLater] = useState<
    SaveForLaterProductData[]
  >([]);

  const notLoggedInFavoriteProductManager = useMemo(() => {
    if (storefrontClient) {
      return new NotLoggedInFavoriteProductManager(storefrontClient);
    }
    return undefined;
  }, [storefrontClient]);

  const shopifyExtensionTeamInfo = useLoad(async () => {
    return await redoShopifyRpcClient(getShopifyExtensionTeamInfo, null);
  }, []);

  const closePanel = useHandler(() => {
    setPanelOpen(false);
    SaveForLaterCache.handleResolvedSlideInPanel();
  });

  const handleSaveForLater = useHandler(
    async (customerWidgetAuthToken: string) => {
      const _teamId = shopifyExtensionTeamInfo.value?.teamId;
      const decodedCustomerWidgetAuthToken = parseAuth(customerWidgetAuthToken);
      if (
        !_teamId ||
        typeof decodedCustomerWidgetAuthToken.customerEmail !== "string" ||
        !shopifyExtensionTeamInfo.value
      ) {
        console.error("Missing team id or invalid auth token");
        return;
      }
      const email = decodedCustomerWidgetAuthToken.customerEmail;

      try {
        await redoShopifyRpcClient(
          addProductsToCustomerProductList,
          {
            productsToAdd: productsToSaveForLater.map((product) => ({
              productHandle: product.productHandle,
              variantId: product.productVariantId,
            })),
            teamId: _teamId,
            email,
          },
          {
            requestHeaders: {
              Authorization: `Bearer ${customerWidgetAuthToken}`,
            },
          },
        );

        const { customerAuthToken, customerInfo } = await redoShopifyRpcClient(
          getCustomerAuthFromCustomerWidgetAuth,
          { customerWidgetAuthToken },
        );

        setViewSavedItemsUrl(
          getViewSavedItemsUrl(
            customerAuthToken,
            customerInfo,
            shopifyExtensionTeamInfo.value.storeUrl,
          ),
        );

        setPanelState(SaveForLaterSlideInPanelState.SUCCESS_CONFIRMATION);
        SaveForLaterCache.handleResolvedSlideInPanel();
      } catch (error) {
        console.error("Failed to save products for later", error);
        closePanel();
      }
    },
  );

  const setAuth = useHandler((authToken: string | undefined) => {
    if (authToken) {
      setLocalStorageJson(CUSTOMER_WIDGET_TOKEN_KEY, authToken);
    }
  });

  const onExternalLoginOrSignupClick = useHandler(async () => {
    if (notLoggedInFavoriteProductManager) {
      for (const product of productsToSaveForLater) {
        notLoggedInFavoriteProductManager.addFavoriteProduct(
          product.productHandle,
          product.productVariantId,
        );
      }
    } else {
      console.error(
        "Not logged in favorite product manager does not exist. Unable to save products for later.",
      );
    }
    closePanel();
  });

  const productImages = useMemo(() => {
    return productsToSaveForLater
      .map((product) => product.productImgSrc)
      .filter(isDef);
  }, [productsToSaveForLater]);

  const oneTimePasscodeParams = useMemo(() => {
    const _teamId = shopifyExtensionTeamInfo.value?.teamId;
    if (_teamId) {
      return {
        submitOneTimePasscodeEmail: async (params: {
          email: string;
        }): Promise<{ fourDigitCodeId: string }> => {
          return await redoShopifyRpcClient(createOneTimePasscode, {
            email: params.email,
            teamId: _teamId,
          });
        },
        verifyOneTimePasscode: async (params: {
          fourDigitCodeId: string;
          passcode: string;
          email: string;
          teamId: string;
          currentLocation?: string;
        }): Promise<{
          success: boolean;
          customerAuthToken: string | undefined;
          multipassUrl: string | undefined;
        }> => {
          const result = await redoShopifyRpcClient(verifyOneTimePasscode, {
            fourDigitCodeId: params.fourDigitCodeId,
            passcode: params.passcode,
            customerEmail: params.email,
            teamId: params.teamId,
            currentLocation: params.currentLocation,
          });
          return {
            success: result.success,
            customerAuthToken: result.customerAuthToken,
            multipassUrl: result.multipassUrl,
          };
        },
      };
    } else {
      return undefined;
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyExtensionTeamInfo]);

  // on component mount, add the product if the user is on a product page
  useEffect(() => {
    const cacheProductAndMaybeOpenPanel = () => {
      if (
        isOnProductPage(onTapcart) &&
        SaveForLaterCache.shouldTrackSaveForLater()
      ) {
        const productHandle = shopifyProductHandle(location.href);
        const productVariantId =
          getVariantIdFromUrl(location.href) ?? undefined;
        void extensionShopifyClient
          .productGet(productHandle)
          .then((product) => {
            const _productsToSaveForLater =
              SaveForLaterCache.addProductToSaveForLaterCache({
                productId: product.id.toString(),
                productHandle,
                productVariantId,
                productImgSrc: product.image?.src,
              });
            setProductsToSaveForLater(_productsToSaveForLater);

            if (
              _productsToSaveForLater.length >=
                (saveForLaterPanelSettings.numViewedProductsUntilPanelOpen ??
                  DEFAULT_NUM_VIEWED_PRODUCTS_UNTIL_PANEL_OPEN) &&
              !SaveForLaterCache.hasResolvedSlideInPanel()
            ) {
              setPanelOpen(true);
            }
          });
      }
    };

    // wait 3 seconds before:
    // - the product is considered one that they've viewed/showed interest in
    // - the panel is (possibly) shown
    const threeSeconds = 3000;
    const timeout = setTimeout(cacheProductAndMaybeOpenPanel, threeSeconds);
    return () => clearTimeout(timeout);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panelTitle = useMemo(() => {
    switch (panelState) {
      case SaveForLaterSlideInPanelState.LOGIN_TO_SAVE_FOR_LATER:
        return saveForLaterPanelSettings.title ?? DEFAULT_PANEL_TITLE;
      case SaveForLaterSlideInPanelState.SUCCESS_CONFIRMATION:
        return "Items saved";
    }
  }, [panelState, saveForLaterPanelSettings]);

  const panelMessage = useMemo(() => {
    switch (panelState) {
      case SaveForLaterSlideInPanelState.LOGIN_TO_SAVE_FOR_LATER:
        return saveForLaterPanelSettings.message ?? DEFAULT_PANEL_MESSAGE;
      case SaveForLaterSlideInPanelState.SUCCESS_CONFIRMATION:
        return (
          <>
            {"These items have been saved to your "}
            <ExternalLink showIcon={false} url={viewSavedItemsUrl}>
              Saved items
            </ExternalLink>
            {" list."}
          </>
        );
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelState, saveForLaterPanelSettings]);

  return shopifyExtensionTeamInfo.value && oneTimePasscodeParams ? (
    <SlideInPanel
      anchorLocation={
        saveForLaterPanelSettings.anchorLocation ??
        DEFAULT_SAVE_FOR_LATER_PANEL_ANCHOR_LOCATION
      }
      handleCloseClick={closePanel}
      open={panelOpen}
    >
      <Flex dir="column" flex={1} gap="xl" w="full">
        <Flex align="center" dir="column">
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            {panelTitle}
          </Text>
          <Text fontSize="xs" fontWeight="regular" textColor="secondary">
            {panelMessage}
          </Text>
        </Flex>
        <Flex justify="center" w="full">
          <Flex gap="lg" overflow="auto">
            {productImages.map((productImage, index) => (
              <Flex
                className={saveForLaterCss.productImageContainer}
                key={index}
                radius="sm"
              >
                <img
                  className={saveForLaterCss.productImage}
                  src={productImage}
                />
              </Flex>
            ))}
          </Flex>
        </Flex>
        {panelState ===
          SaveForLaterSlideInPanelState.LOGIN_TO_SAVE_FOR_LATER && (
          <Flex dir="column" gap="lg">
            {shopifyExtensionTeamInfo.value.shouldLoginUsingMultipass ? (
              <RedoLogin
                onLogin={handleSaveForLater}
                otpParams={oneTimePasscodeParams}
                setAuth={setAuth}
                showMarketingCheckbox={saveForLaterMarketingEnabled}
                submitButtonParams={{
                  sendOneTimePasscodeButtonText: "Save for later",
                  size: ButtonSize.NANO,
                }}
                subscribeToMarketing={(email: string, agreementText: string) =>
                  redoShopifyRpcClient(subscribeToEmailMarketing, {
                    email,
                    entryPoint: MarketingEntryPoint.SAVE_FOR_LATER,
                    agreementText,
                  })
                }
                teamId={shopifyExtensionTeamInfo.value.teamId}
                textParams={{ fontSize: "xs", fontWeight: "medium" }}
              />
            ) : (
              <ExternalLogin
                centerSignInText
                onLoginClick={onExternalLoginOrSignupClick}
                onRegisterClick={onExternalLoginOrSignupClick}
                registerLinkText="Create account"
              />
            )}
          </Flex>
        )}
      </Flex>
    </SlideInPanel>
  ) : null;
};
